import { useMemo, useState } from 'react'
import {
  BadgeStatus,
  Button,
  ConfirmDialog,
  DataTable,
  FilterBar,
  Input,
  KolomForm,
  Modal,
  PageHeader,
  Select,
  Textarea,
  FILTER_KOSONG,
  type KolomTabel,
  type NilaiFilter,
} from '@/components'
import { api, buatIdempotencyKey } from '@/lib/api'
import { formatRupiah, formatTanggal } from '@/lib/format'
import type { AkunKas, Transaksi } from '@/lib/types'
import { useApi, usePaged, useAksi } from '@/lib/useApi'
import { useOpsiUnit, useOpsiAkun } from '@/lib/opsi'
import { useAuth } from '@/lib/auth'

type JenisKas = 'masuk' | 'keluar'

const TIPE_MASUK = ['CASH_IN', 'BANK_IN', 'ADJUSTMENT']
const TIPE_KELUAR = ['CASH_OUT', 'BANK_OUT', 'OPERATIONAL_EXPENSE', 'SUPPLIER_PAYMENT']

/**
 * Halaman bersama Kas Masuk / Kas Keluar.
 * Perbedaan utama: jenis transaksi dan pemilihan akun beban (hanya kas keluar).
 */
export function TransaksiKas({ jenis }: { jenis: JenisKas }) {
  const { punyaIzin } = useAuth()
  const opsiUnit = useOpsiUnit()
  const aksi = useAksi()

  const [filter, setFilter] = useState<NilaiFilter>({ ...FILTER_KOSONG, dari: `${new Date().getFullYear()}-01-01` })
  const [statusFilter, setStatusFilter] = useState('')
  const [modal, setModal] = useState(false)
  const [konfirmasi, setKonfirmasi] = useState<{ aksi: string; data: Transaksi } | null>(null)

  const tipeBawaan = jenis === 'masuk' ? 'CASH_IN' : 'CASH_OUT'

  const daftar = usePaged<Transaksi>('/transactions', {
    query: {
      dari: filter.dari || undefined,
      sampai: filter.sampai || undefined,
      unitId: filter.unitId || undefined,
      status: statusFilter || undefined,
      jenis: jenis === 'masuk' ? 'MASUK' : 'KELUAR',
      transactionType: tipeBawaan,
    },
  })

  const kolom = useMemo<KolomTabel<Transaksi>[]>(
    () => [
      { kunci: 'transactionNumber', judul: 'Nomor', render: (b) => <span className="font-medium text-netral-800">{b.transactionNumber}</span> },
      { kunci: 'transactionDate', judul: 'Tanggal', render: (b) => formatTanggal(b.transactionDate) },
      { kunci: 'description', judul: 'Uraian' },
      { kunci: 'cashAccount', judul: jenis === 'masuk' ? 'Kas/Bank Penerima' : 'Kas/Bank Sumber', render: (b) => b.cashAccount?.name ?? '—' },
      ...(jenis === 'keluar'
        ? [{ kunci: 'account', judul: 'Akun Beban', render: (b: Transaksi) => (b.account ? `${b.account.code} — ${b.account.name}` : '—') } as KolomTabel<Transaksi>]
        : []),
      {
        kunci: 'amount',
        judul: 'Jumlah',
        rata: 'kanan' as const,
        render: (b: Transaksi) => (
          <span className={jenis === 'masuk' ? 'font-medium text-yayasan-800' : 'font-medium text-red-700'}>
            {formatRupiah(b.amount)}
          </span>
        ),
      },
      { kunci: 'status', judul: 'Status', render: (b) => <BadgeStatus status={b.status} /> },
      {
        kunci: 'aksi',
        judul: 'Aksi',
        sortable: false,
        render: (b) => (
          <div className="flex flex-wrap gap-1.5">
            {punyaIzin('transaction:submit') && b.status === 'DRAFT' ? (
              <Button ukuran="kecil" onClick={() => setKonfirmasi({ aksi: 'submit', data: b })}>
                Ajukan
              </Button>
            ) : null}
            {punyaIzin('transaction:review') && b.status === 'SUBMITTED' ? (
              <Button ukuran="kecil" varian="garis" onClick={() => setKonfirmasi({ aksi: 'review', data: b })}>
                Tinjau
              </Button>
            ) : null}
            {punyaIzin('transaction:approve') && b.status === 'REVIEWED' ? (
              <Button ukuran="kecil" varian="sekunder" onClick={() => setKonfirmasi({ aksi: 'approve', data: b })}>
                Setujui
              </Button>
            ) : null}
            {punyaIzin('transaction:post') && (b.status === 'APPROVED' || b.status === 'PAID') ? (
              <Button ukuran="kecil" varian="utama" onClick={() => setKonfirmasi({ aksi: 'post', data: b })}>
                Posting
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [jenis, punyaIzin],
  )

  const label = jenis === 'masuk' ? 'Kas Masuk' : 'Kas Keluar'

  return (
    <div>
      <PageHeader
        judul={label}
        deskripsi={
          jenis === 'masuk'
            ? 'Pencatatan penerimaan kas/bank di luar tagihan santri (donasi, hibah, pendapatan lain).'
            : 'Pencatatan pengeluaran kas/bank beserta pemilihan akun beban.'
        }
        ekspor={{
          path: jenis === 'masuk' ? '/reports/cash-book' : '/reports/cash-book',
          namaDasar: jenis === 'masuk' ? 'kas-masuk' : 'kas-keluar',
          query: {
            dari: filter.dari || undefined,
            sampai: filter.sampai || undefined,
            unitId: filter.unitId || undefined,
            jenis,
          },
        }}
        aksi={
          punyaIzin('transaction:create') ? (
            <Button onClick={() => setModal(true)}>+ Catat {label}</Button>
          ) : null
        }
      />

      <FilterBar
        nilai={filter}
        onUbah={setFilter}
        unit={opsiUnit}
        tampilkan={{ kategori: false, program: false, fundId: false }}
        ekstra={
          <Select
            label="Status"
            placeholder="Semua status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            opsi={['DRAFT', 'SUBMITTED', 'REVIEWED', 'APPROVED', 'POSTED', 'RECONCILED', 'REJECTED', 'CANCELLED'].map((s) => ({
              value: s,
              label: s,
            }))}
            className="min-w-40"
          />
        }
      />

      <DataTable
        kolom={kolom}
        data={daftar.items}
        kunciBaris={(b) => b.id}
        memuat={daftar.memuat}
        galat={daftar.galat}
        kodeGalat={daftar.kodeGalat}
        onCoba={daftar.muat}
        meta={daftar.meta}
        onHalaman={daftar.setHalaman}
        onUrut={daftar.setUrutan}
        urutSaatIni={daftar.urutan}
        pesanKosong={`Belum ada transaksi ${label.toLowerCase()} pada filter ini.`}
      />

      <FormTransaksi
        jenis={jenis}
        terbuka={modal}
        onTutup={() => setModal(false)}
        setelah={daftar.muat}
      />

      <ConfirmDialog
        terbuka={konfirmasi !== null}
        onTutup={() => setKonfirmasi(null)}
        judul={`Konfirmasi ${konfirmasi?.aksi ?? ''}`}
        pesan={`Lanjutkan aksi "${konfirmasi?.aksi}" untuk transaksi ${konfirmasi?.data.transactionNumber} sebesar ${formatRupiah(konfirmasi?.data.amount ?? 0)}?`}
        labelKonfirmasi="Lanjutkan"
        berbahaya={false}
        memuat={aksi.memuat}
        onKonfirmasi={async () => {
          if (!konfirmasi) return
          const { aksi: nama, data } = konfirmasi
          const hasil = await aksi.jalankan(
            () => api.post(`/transactions/${data.id}/${nama}`, {}, { idempotencyKey: buatIdempotencyKey() }),
            { sukses: `Transaksi ${data.transactionNumber} berhasil diproses.` },
          )
          if (hasil !== null) {
            setKonfirmasi(null)
            daftar.muat()
          }
        }}
      />
    </div>
  )
}

function FormTransaksi({
  jenis,
  terbuka,
  onTutup,
  setelah,
}: {
  jenis: JenisKas
  terbuka: boolean
  onTutup: () => void
  setelah: () => void
}) {
  const opsiUnit = useOpsiUnit()
  const akunKas = useApi<unknown>('/cash-accounts', { perPage: 200 })
  const daftarKas: AkunKas[] = Array.isArray(akunKas.data)
    ? (akunKas.data as AkunKas[])
    : ((akunKas.data as { items?: AkunKas[] } | null)?.items ?? [])

  const beban = useOpsiAkun({ hanyaPostable: true, tipe: 'EXPENSE' })
  const dana = useApi<unknown>('/funds', { perPage: 200 })
  const daftarDana = Array.isArray(dana.data)
    ? (dana.data as Array<{ id: string; code: string; name: string }>)
    : ((dana.data as { items?: Array<{ id: string; code: string; name: string }> } | null)?.items ?? [])
  const pusatBiaya = useApi<unknown>('/cost-centers', { perPage: 200 })
  const daftarPB = Array.isArray(pusatBiaya.data)
    ? (pusatBiaya.data as Array<{ id: string; code: string; name: string }>)
    : ((pusatBiaya.data as { items?: Array<{ id: string; code: string; name: string }> } | null)?.items ?? [])

  const [unitId, setUnitId] = useState('')
  const [transactionType, setTransactionType] = useState(jenis === 'masuk' ? 'CASH_IN' : 'CASH_OUT')
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().slice(0, 10))
  const [description, setDescription] = useState('')
  const [cashAccountId, setCashAccountId] = useState('')
  const [accountId, setAccountId] = useState('')
  const [fundId, setFundId] = useState('')
  const [costCenterId, setCostCenterId] = useState('')
  const [amount, setAmount] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('CASH')
  const [referenceNumber, setReferenceNumber] = useState('')
  const [notes, setNotes] = useState('')
  const [galat, setGalat] = useState<string | null>(null)
  const [memuat, setMemuat] = useState(false)

  const simpan = async () => {
    setGalat(null)
    if (!unitId) return setGalat('Unit wajib dipilih.')
    if (!description.trim()) return setGalat('Uraian transaksi wajib diisi.')
    const nilai = Number(amount.replace(/[^0-9.-]/g, ''))
    if (!nilai || nilai <= 0) return setGalat('Jumlah harus lebih dari nol.')
    if (jenis === 'keluar' && !accountId) return setGalat('Akun beban wajib dipilih untuk kas keluar.')

    setMemuat(true)
    try {
      await api.post(
        '/transactions',
        {
          unitId,
          transactionType,
          transactionDate,
          description: description.trim(),
          cashAccountId: cashAccountId || undefined,
          accountId: accountId || undefined,
          fundId: fundId || undefined,
          costCenterId: costCenterId || undefined,
          amount: nilai,
          paymentMethod,
          referenceNumber: referenceNumber.trim() || undefined,
          notes: notes.trim() || undefined,
        },
        { idempotencyKey: buatIdempotencyKey() },
      )
      setDescription('')
      setAmount('')
      setelah()
      onTutup()
    } catch (e) {
      setGalat(e instanceof Error ? e.message : 'Gagal menyimpan transaksi.')
    } finally {
      setMemuat(false)
    }
  }

  return (
    <Modal
      terbuka={terbuka}
      onTutup={onTutup}
      judul={jenis === 'masuk' ? 'Catat Kas Masuk' : 'Catat Kas Keluar'}
      deskripsi="Transaksi tersimpan sebagai draf, lalu diajukan, ditinjau, disetujui, dan diposting."
      lebar="max-w-3xl"
      footer={
        <>
          <Button varian="garis" onClick={onTutup} disabled={memuat}>
            Batal
          </Button>
          <Button onClick={() => void simpan()} memuat={memuat}>
            Simpan
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {galat ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {galat}
          </div>
        ) : null}

        <KolomForm kolom={2}>
          <Select
            label="Unit"
            placeholder="Pilih unit"
            opsi={opsiUnit}
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
            required
          />
          <Select
            label="Jenis Transaksi"
            opsi={(jenis === 'masuk' ? TIPE_MASUK : TIPE_KELUAR).map((t) => ({ value: t, label: t }))}
            value={transactionType}
            onChange={(e) => setTransactionType(e.target.value)}
          />
          <Input
            type="date"
            label="Tanggal Transaksi"
            value={transactionDate}
            onChange={(e) => setTransactionDate(e.target.value)}
            required
          />
          <Input
            label="Jumlah (Rp)"
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
          <Select
            label={jenis === 'masuk' ? 'Kas/Bank Penerima' : 'Kas/Bank Sumber'}
            placeholder="Pilih akun kas/bank"
            opsi={daftarKas.map((k) => ({ value: k.id, label: `${k.code} — ${k.name}` }))}
            value={cashAccountId}
            onChange={(e) => setCashAccountId(e.target.value)}
            bantuan={akunKas.galat ? `Gagal memuat akun kas: ${akunKas.galat}` : undefined}
          />
          {jenis === 'keluar' ? (
            <Select
              label="Akun Beban"
              placeholder={beban.memuat ? 'Memuat akun…' : 'Pilih akun beban'}
              opsi={beban.opsi}
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              required
              bantuan={beban.galat ? `Gagal memuat akun: ${beban.galat}` : 'Hanya akun beban yang dapat diposting.'}
            />
          ) : null}
          <Select
            label="Sumber Dana"
            placeholder="Opsional"
            opsi={daftarDana.map((d) => ({ value: d.id, label: `${d.code} — ${d.name}` }))}
            value={fundId}
            onChange={(e) => setFundId(e.target.value)}
          />
          <Select
            label="Pusat Biaya"
            placeholder="Opsional"
            opsi={daftarPB.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
            value={costCenterId}
            onChange={(e) => setCostCenterId(e.target.value)}
          />
          <Select
            label="Metode Pembayaran"
            opsi={['CASH', 'BANK_TRANSFER', 'CHEQUE', 'OTHER'].map((m) => ({ value: m, label: m }))}
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
          />
          <Input
            label="Nomor Referensi"
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
          />
        </KolomForm>

        <Textarea
          label="Uraian Transaksi"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          rows={2}
        />
        <Textarea label="Catatan" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </div>
    </Modal>
  )
}
